<template>
    <div class="task-reports">
        <!-- Filtre Barı -->
        <div class="filter-bar">
            <div class="filter-bar__left">
                <a-icon type="bar-chart" class="filter-bar__icon" />
                <span class="filter-bar__title">{{ $t('task_reports.title') }}</span>
            </div>
            <div class="filter-bar__right">
                <div class="filter-item">
                    <span class="filter-item__label">{{ $t('task_reports.date') }}</span>
                    <a-range-picker
                        :value="dateRange"
                        format="DD.MM.YYYY"
                        :allowClear="false"
                        size="small"
                        style="width: 240px"
                        :ranges="quickDateRanges"
                        :disabledDate="disabledDate"
                        @change="handleDateRangeChange"
                    />
                </div>
                <div class="filter-item">
                    <span class="filter-item__label">{{ $t('task_reports.workflow') }}</span>
                    <a-select
                        v-model="selectedWorkflowId"
                        :placeholder="$t('task_reports.select')"
                        style="width: 170px"
                        size="small"
                        @change="handleWorkflowChange"
                    >
                        <a-select-option v-for="wf in availableWorkflows" :key="wf._id" :value="wf._id">
                            {{ wf.title }}
                        </a-select-option>
                    </a-select>
                </div>
                <div class="filter-item">
                    <span class="filter-item__label">{{ $t('task_reports.agent') }}</span>
                    <a-select
                        v-model="selectedUsers"
                        mode="multiple"
                        :placeholder="$t('task_reports.all')"
                        style="width: 200px"
                        :maxTagCount="1"
                        allowClear
                        size="small"
                        @change="handleUserFilterChange"
                    >
                        <a-select-option v-for="user in availableUsers" :key="user._id" :value="user._id">
                            {{ user.name }}
                        </a-select-option>
                    </a-select>
                </div>
            </div>
        </div>

        <!-- İş akışı seçilmemiş -->
        <div v-if="!selectedWorkflowId" class="empty-workflow">
            <a-icon type="folder-open" class="empty-workflow__icon" />
            <p class="empty-workflow__text">{{ $t('task_reports.select_workflow_to_view_reports') }}</p>
        </div>

        <!-- Ana Loading -->
        <div v-else-if="isLoading" class="report-body">
            <div class="section-row section-row--cards">
                <div class="skel-card"><div class="skel-line skel-line--lg"></div><div class="skel-line skel-line--sm"></div></div>
                <div class="skel-card"><div class="skel-line skel-line--lg"></div><div class="skel-line skel-line--sm"></div></div>
            </div>
            <div class="skel-section"><div class="skel-section__head"></div><div class="skel-row"></div><div class="skel-row"></div><div class="skel-row skel-row--short"></div></div>
            <div class="skel-section"><div class="skel-section__head"></div><div class="skel-row"></div><div class="skel-row skel-row--short"></div></div>
        </div>

        <!-- İş akışı seçili -->
        <div v-else-if="reportData" class="report-body">

                <!-- BÖLÜM 1: Özet Kartları -->
                <div class="section-row section-row--cards">
                    <div class="stat-card stat-card--blue">
                        <div class="stat-card__number">{{ reportData.summary.totalCreated }}</div>
                        <div class="stat-card__label">{{ $t('task_reports.created_task') }}</div>
                        <div class="stat-card__sub">{{ dateRangeLabel }}</div>
                    </div>
                    <div class="stat-card stat-card--orange">
                        <div class="stat-card__number">{{ reportData.summary.totalChanged }}</div>
                        <div class="stat-card__label">{{ $t('task_reports.updated_task') }}</div>
                        <div class="stat-card__sub">{{ dateRangeLabel }}</div>
                    </div>
                </div>

                <!-- BÖLÜM 2: Personel Bazında -->
                <div class="report-section">
                    <div class="report-section__title">{{ $t('task_reports.assigned_tasks_by_personnel') }}</div>
                    <a-table
                        v-if="reportData.summary.byPersonnel.length > 0"
                        :columns="personnelColumns"
                        :dataSource="reportData.summary.byPersonnel"
                        :pagination="false"
                        :rowKey="(r, i) => (r.userId || 'u') + '_' + i"
                        size="small"
                        bordered
                    >
                        <template slot="userName" slot-scope="text, record">
                            <div class="user-cell">
                                <a-avatar v-if="record.gravatar" :src="record.gravatar" :size="22" />
                                <a-avatar v-else :size="22" icon="user" />
                                <span>{{ text }}</span>
                            </div>
                        </template>
                        <template slot="createdCount" slot-scope="text">
                            <a-tag color="blue">{{ text }}</a-tag>
                        </template>
                    </a-table>
                    <div v-else class="mini-empty">{{ $t('task_reports.no_assigned_task_found_in_date_range') }}</div>
                </div>

                <!-- BÖLÜM 3: Güncellenen Görevler -->
                <div class="report-section">
                    <div class="report-section__title">
                        {{ $t('task_reports.updated_tasks') }}
                        <span v-if="reportData.summary.changedTasks.length > 0" class="report-section__count">
                            ({{ reportData.summary.changedTasks.length }})
                        </span>
                    </div>
                    <a-table
                        v-if="reportData.summary.changedTasks.length > 0"
                        :columns="changedColumns"
                        :dataSource="reportData.summary.changedTasks"
                        :pagination="reportData.summary.changedTasks.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] } : false"
                        :rowKey="(r, i) => (r.taskId || '') + '_' + i"
                        size="small"
                        bordered
                    >
                        <template slot="taskTitle" slot-scope="text, record">
                            <div class="task-link" @click="openTaskDetail(record)">
                                <strong>{{ text }}</strong>
                                <span v-if="record.customId" class="text-muted" style="margin-left: 4px;">#{{ record.customId }}</span>
                            </div>
                        </template>
                        <template slot="currentStageName" slot-scope="text, record">
                            <a-tooltip :title="text">
                                <a-tag :color="record.currentStageColor" class="stage-tag-ellipsis">{{ text }}</a-tag>
                            </a-tooltip>
                        </template>
                        <template slot="updatedAt" slot-scope="text">
                            {{ formatDateTime(text) }}
                        </template>
                    </a-table>
                    <div v-else class="mini-empty">{{ $t('task_reports.no_updated_task_found_in_date_range') }}</div>
                </div>

                <!-- BÖLÜM 4: Temsilci Bazında Görev Dağılımı -->
                <div class="report-section">
                    <div class="report-section__title">{{ $t('task_reports.task_distribution_by_agent') }}</div>
                    <a-table
                        v-if="agentTableData.length > 0"
                        :columns="agentTableColumns"
                        :dataSource="agentTableData"
                        :pagination="false"
                        :rowKey="(r) => r.userId || 'unassigned'"
                        size="small"
                        bordered
                    >
                        <template slot="userName" slot-scope="text, record">
                            <div class="user-cell">
                                <a-avatar v-if="record.gravatar" :src="record.gravatar" :size="22" />
                                <a-avatar v-else :size="22" icon="user" />
                                <strong>{{ text }}</strong>
                            </div>
                        </template>
                        <template slot="totalTasks" slot-scope="text">
                            <strong>{{ text }}</strong>
                        </template>
                    </a-table>
                    <div v-else class="mini-empty">{{ $t('task_reports.no_assigned_task_found') }}</div>
                </div>

                <!-- BÖLÜM 5: Zaman Çizelgesi -->
                <div class="report-section">
                    <div class="report-section__title report-section__title--flex">
                        <span>{{ $t('task_reports.timeline') }}</span>
                        <div class="tl-inline-controls">
                            <a-select
                                v-model="timelineUserId"
                                :placeholder="$t('task_reports.select')"
                                style="width: 200px"
                                showSearch
                                allowClear
                                size="small"
                                :filterOption="filterUserOption"
                                @change="loadTimeline"
                            >
                                <a-select-option key="unassigned" value="unassigned">
                                    {{ $t('task_reports.unassigned_tasks') }}
                                </a-select-option>
                                <a-select-option v-for="user in availableUsers" :key="user._id" :value="user._id">
                                    {{ user.name }}
                                </a-select-option>
                            </a-select>
                            <a-radio-group v-model="timelineView" size="small">
                                <a-radio-button value="gantt">{{ $t('task_reports.gantt') }}</a-radio-button>
                                <a-radio-button value="table">{{ $t('task_reports.table') }}</a-radio-button>
                            </a-radio-group>
                        </div>
                    </div>

                    <div v-if="isTimelineLoading" class="tl-loading">
                        <div class="skel-row"></div>
                        <div class="skel-row skel-row--short"></div>
                        <div class="skel-row"></div>
                    </div>
                    <template v-else>
                        <div v-if="timelineData && timelineData.tasks.length > 0">
                            <!-- Gantt -->
                            <div v-if="timelineView === 'gantt'" class="gantt-wrap">
                                <div class="gantt-legend">
                                    <span v-for="leg in ganttLegend" :key="leg.name" class="gantt-legend__item">
                                        <span class="gantt-legend__dot" :style="{ background: leg.color }"></span>
                                        {{ leg.name }}
                                    </span>
                                </div>
                                <div
                                    v-for="(group, gi) in groupedTimelineTasks"
                                    :key="'g' + gi"
                                    class="gantt-group"
                                >
                                    <div class="gantt-group__head">
                                        <span class="gantt-group__dot" :style="{ background: group.stageColor }"></span>
                                        {{ group.stageName }}
                                        <span class="gantt-group__cnt">({{ group.tasks.length }})</span>
                                    </div>
                                    <div
                                        v-for="task in group.tasks"
                                        :key="task.taskId"
                                        class="gantt-row"
                                    >
                                        <div class="gantt-row__label" :title="task.taskTitle">
                                            <span class="gantt-row__title">{{ task.taskTitle }}</span>
                                            <span class="gantt-row__id" v-if="task.customId">{{ task.customId }}</span>
                                        </div>
                                        <div class="gantt-row__bars">
                                            <a-tooltip
                                                v-for="(stg, si) in task.stageHistory"
                                                :key="si"
                                                placement="top"
                                            >
                                                <template slot="title">
                                                    <strong>{{ stg.stageName }}</strong><br/>
                                                    {{ $t('task_reports.entry') }}: {{ formatDateTime(stg.enteredAt) }}<br/>
                                                    <template v-if="stg.leftAt">{{ $t('task_reports.exit') }}: {{ formatDateTime(stg.leftAt) }}<br/></template>
                                                    <template v-else>{{ $t('task_reports.still_in_this_stage') }}<br/></template>
                                                    {{ $t('task_reports.duration') }}: {{ formatDuration(stg.durationDays, stg.durationHours) }}
                                                </template>
                                                <div
                                                    class="gantt-seg"
                                                    :style="{ background: stg.stageColor, flex: getSegmentFlex(stg) }"
                                                >
                                                    <span class="gantt-seg__txt" v-if="stg.durationDays > 0 || stg.durationHours > 2">
                                                        {{ formatDurationShort(stg.durationDays, stg.durationHours) }}
                                                    </span>
                                                </div>
                                            </a-tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Table -->
                            <div v-if="timelineView === 'table'">
                                <a-table
                                    :columns="timelineTableColumns"
                                    :dataSource="timelineData.tasks"
                                    :pagination="{ pageSize: 15, size: 'small' }"
                                    :rowKey="(r) => r.taskId"
                                    size="small"
                                    bordered
                                >
                                    <template slot="taskTitle" slot-scope="text, record">
                                        <strong>{{ text }}</strong>
                                        <span v-if="record.customId" class="tl-cid">{{ record.customId }}</span>
                                    </template>
                                    <template slot="currentStage" slot-scope="text, record">
                                        <a-tag v-if="record.currentStage" :color="record.currentStage.stageColor">
                                            {{ record.currentStage.stageName }}
                                        </a-tag>
                                    </template>
                                    <template slot="stageHistory" slot-scope="text, record">
                                        <div class="stage-flow">
                                            <span v-for="(stg, si) in record.stageHistory" :key="si" class="stage-flow__item">
                                                <a-tag :color="stg.stageColor" size="small">
                                                    {{ stg.stageName }} ({{ formatDuration(stg.durationDays, stg.durationHours) }})
                                                </a-tag>
                                                <span v-if="si < record.stageHistory.length - 1" class="stage-flow__arrow">→</span>
                                            </span>
                                        </div>
                                    </template>
                                </a-table>
                            </div>
                        </div>
                        <div v-else-if="timelineUserId && !isTimelineLoading" class="mini-empty">
                            {{ $t('task_reports.no_active_task_found_for_agent') }}
                        </div>
                        <div v-else-if="!timelineUserId" class="mini-empty">
                            {{ $t('task_reports.select_agent_to_view_timeline') }}
                        </div>
                    </template>
                </div>

            </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
    name: "TaskReports",
    props: {
        workflowId: {
            type: String,
            default: undefined,
        },
    },
    data() {
        return {
            dateRange: [this.$moment(), this.$moment()],
            selectedWorkflowId: undefined,
            selectedUsers: [],
            isLoading: false,
            reportData: null,
            timelineUserId: undefined,
            timelineData: null,
            isTimelineLoading: false,
            timelineView: "gantt",
        };
    },
    computed: {
        ...mapState({
            storeWorkflows: function (state) { return state.tasks.workflows; },
            usersData: function (state) { return state.user.users.data; },
        }),
        personnelColumns: function() {
            return [
                { title: this.$t('task_reports.agent'), dataIndex: "userName", key: "userName", scopedSlots: { customRender: "userName" } },
                { title: this.$t('task_reports.assigned_task'), dataIndex: "createdCount", key: "createdCount", scopedSlots: { customRender: "createdCount" }, align: "center", width: 120 },
            ];
        },
        changedColumns: function() {
            return [
                { title: this.$t('task_reports.task'), dataIndex: "taskTitle", key: "taskTitle", scopedSlots: { customRender: "taskTitle" }, ellipsis: true },
                { title: this.$t('task_reports.current_stage'), dataIndex: "currentStageName", key: "currentStageName", scopedSlots: { customRender: "currentStageName" }, width: 140, align: "center" },
                { title: this.$t('task_reports.assigned'), dataIndex: "assigneeNames", key: "assigneeNames", width: 160, ellipsis: true },
                { title: this.$t('task_reports.update'), dataIndex: "updatedAt", key: "updatedAt", scopedSlots: { customRender: "updatedAt" }, width: 130, align: "center" },
            ];
        },
        timelineTableColumns: function() {
            return [
                { title: this.$t('task_reports.task'), dataIndex: "taskTitle", key: "taskTitle", scopedSlots: { customRender: "taskTitle" }, width: 180, ellipsis: true },
                { title: this.$t('task_reports.current_stage'), dataIndex: "currentStage", key: "currentStage", scopedSlots: { customRender: "currentStage" }, width: 120 },
                { title: this.$t('task_reports.transition_process'), dataIndex: "stageHistory", key: "stageHistory", scopedSlots: { customRender: "stageHistory" } },
            ];
        },
        quickDateRanges: function () {
            const m = this.$moment;
            return {
                "Bugün": [m(), m()],
                "Bu Hafta": [m().startOf("week"), m()],
                "Bu Ay": [m().startOf("month"), m()],
                "Geçen Ay": [m().subtract(1, "month").startOf("month"), m().subtract(1, "month").endOf("month")],
            };
        },
        dateRangeLabel: function () {
            if (!this.dateRange || this.dateRange.length < 2) return "";
            const start = this.dateRange[0].format("DD.MM.YYYY");
            const end = this.dateRange[1].format("DD.MM.YYYY");
            if (start === end) return start;
            return start + " - " + end;
        },
        availableWorkflows: function () {
            if (!Array.isArray(this.storeWorkflows)) return [];
            return this.storeWorkflows;
        },
        availableUsers: function () {
            if (this.reportData && this.reportData.users && this.reportData.users.length > 0) {
                return this.reportData.users;
            }
            if (!Array.isArray(this.usersData)) return [];
            return this.usersData.map(function (u) { return { _id: u._id, name: u.name, gravatar: u.gravatar }; });
        },
        pipelineStagesList: function () {
            if (!this.reportData || !this.reportData.pipelineStages) return [];
            return this.reportData.pipelineStages;
        },
        agentTableColumns: function () {
            const cols = [
                { title: this.$t('task_reports.agent'), dataIndex: "userName", key: "userName", scopedSlots: { customRender: "userName" }, width: 180 },
                { title: this.$t('task_reports.total'), dataIndex: "totalTasks", key: "totalTasks", scopedSlots: { customRender: "totalTasks" }, align: "center", width: 80 },
            ];
            this.pipelineStagesList.forEach(function (stage) {
                cols.push({
                    title: stage.stageName,
                    dataIndex: "stage_" + stage.stageId,
                    key: "stage_" + stage.stageId,
                    customRender: function (text) { return text || 0; },
                    align: "center",
                    width: 100,
                });
            });
            return cols;
        },
        agentTableData: function () {
            if (!this.reportData || !this.reportData.agentReports) return [];
            return this.reportData.agentReports.map(function (agent) {
                const row = {
                    userId: agent.userId,
                    userName: agent.userName,
                    gravatar: agent.gravatar,
                    totalTasks: agent.totalTasks,
                };
                agent.pipelineBreakdown.forEach(function (pb) {
                    row["stage_" + pb.stageId] = pb.count;
                });
                return row;
            });
        },
        groupedTimelineTasks: function () {
            if (!this.timelineData || !this.timelineData.tasks) return [];
            const groups = [];
            let currentStageName = null;
            let currentGroup = null;
            for (let i = 0; i < this.timelineData.tasks.length; i++) {
                const task = this.timelineData.tasks[i];
                const stageName = task.currentStage ? task.currentStage.stageName : this.$t('task_reports.unknown');
                if (stageName !== currentStageName) {
                    currentStageName = stageName;
                    currentGroup = { stageName: stageName, stageColor: task.currentStage ? task.currentStage.stageColor : "#8c8c8c", tasks: [] };
                    groups.push(currentGroup);
                }
                currentGroup.tasks.push(task);
            }
            return groups;
        },
        ganttLegend: function () {
            if (!this.timelineData || !this.timelineData.tasks) return [];
            const map = {};
            this.timelineData.tasks.forEach(function (t) {
                t.stageHistory.forEach(function (s) {
                    if (!map[s.stageName]) map[s.stageName] = { name: s.stageName, color: s.stageColor };
                });
            });
            return Object.keys(map).map(function (k) { return map[k]; });
        },
    },
    methods: {
        ...mapActions({
            fetchTaskReports: "tasks/getTaskReports",
            fetchTaskReportsTimeline: "tasks/getTaskReportsTimeline",
            getWorkFlows: "tasks/getWorkFlows",
            addNewWindow: "app/addNewWindow",
        }),
        openTaskDetail: function (record) {
            if (!record || !record.taskId) return;
            this.addNewWindow({
                type: "editor-task",
                title: record.taskTitle || "Görev",
                payload: {
                    taskId: record.taskId,
                },
            });
        },
        disabledDate: function (current) {
            if (!current) return false;
            return current.isAfter(this.$moment().endOf("day"));
        },
        handleDateRangeChange: function (dates) {
            if (dates && dates.length === 2) {
                const diffDays = dates[1].diff(dates[0], "days");
                if (diffDays > 30) {
                    this.$message.warning(this.$t('task_reports.max_30_days_range_warning'));
                    this.dateRange = [dates[1].clone().subtract(30, "days"), dates[1]];
                } else {
                    this.dateRange = dates;
                }
            } else {
                this.dateRange = [this.$moment(), this.$moment()];
            }
            if (this.selectedWorkflowId) {
                this.loadReports();
                if (this.timelineUserId) this.loadTimeline();
            }
        },
        handleWorkflowChange: function () {
            this.reportData = null;
            this.timelineData = null;
            this.timelineUserId = undefined;
            if (this.selectedWorkflowId) this.loadReports();
        },
        handleUserFilterChange: function () {
            this.loadReports();
            if (this.selectedUsers.length === 1) {
                this.timelineUserId = this.selectedUsers[0];
                this.loadTimeline();
            } else if (this.selectedUsers.length === 0 && this.timelineUserId) {
                this.loadTimeline();
            }
        },
        filterUserOption: function (input, option) {
            const text = option.componentOptions.children[0].text || "";
            return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        loadReports: async function () {
            if (!this.selectedWorkflowId) return;
            this.isLoading = true;
            try {
                const filters = {
                    startDate: this.dateRange[0].clone().startOf("day").toISOString(),
                    endDate: this.dateRange[1].clone().endOf("day").toISOString(),
                    workflowId: this.selectedWorkflowId,
                };
                if (this.selectedUsers.length > 0) filters.users = this.selectedUsers;
                this.reportData = await this.fetchTaskReports({ filters: filters });
            } catch (e) {
                console.error("Reports load error:", e);
                this.$message.error("Raporlar yüklenirken hata oluştu.");
            } finally {
                this.isLoading = false;
            }
        },
        loadTimeline: async function () {
            if (!this.timelineUserId) { this.timelineData = null; return; }
            this.isTimelineLoading = true;
            try {
                const filters = {
                    startDate: this.dateRange[0].clone().startOf("day").toISOString(),
                    endDate: this.dateRange[1].clone().endOf("day").toISOString(),
                };
                if (this.selectedWorkflowId) filters.workflowId = this.selectedWorkflowId;
                this.timelineData = await this.fetchTaskReportsTimeline({ userId: this.timelineUserId, filters: filters });
            } catch (e) {
                console.error("Timeline load error:", e);
                this.$message.error("Zaman çizelgesi yüklenirken hata oluştu.");
            } finally {
                this.isTimelineLoading = false;
            }
        },
        formatTime: function (date) { return date ? this.$moment(date).format("HH:mm") : "-"; },
        formatDateTime: function (date) { return date ? this.$moment(date).format("DD.MM HH:mm") : "-"; },
        formatDuration: function (days, hours) {
            if (days > 0) return hours > 0 ? days + " " + this.$t('task_reports.day') + " " + hours + " " + this.$t('task_reports.hour') : days + " " + this.$t('task_reports.day');
            if (hours > 0) return hours + " " + this.$t('task_reports.hour');
            return "< 1 " + this.$t('task_reports.hour');
        },
        formatDurationShort: function (days, hours) {
            if (days > 0) return days + "g";
            if (hours > 0) return hours + "s";
            return "<1s";
        },
        getSegmentFlex: function (stage) {
            return String(Math.max((stage.durationDays * 24) + stage.durationHours, 1));
        },
    },
    mounted: async function () {
        await this.getWorkFlows();
        if (this.workflowId) {
            this.selectedWorkflowId = this.workflowId;
            this.loadReports();
        } else if (this.availableWorkflows.length > 0) {
            this.selectedWorkflowId = this.availableWorkflows[0]._id;
            this.loadReports();
        }
    },
};
</script>

<style lang="scss" scoped>
.task-reports {
    padding: 20px 24px;
    background: #f5f7fa;
    min-height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Filtre Barı */
.filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border-radius: 12px;
    border: none;
    padding: 14px 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);

    &__left {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__icon {
        font-size: 20px;
        color: #4f6af6;
        background: rgba(79, 106, 246, 0.08);
        padding: 6px;
        border-radius: 8px;
    }

    &__title {
        font-size: 17px;
        font-weight: 700;
        color: #1e293b;
        letter-spacing: -0.2px;
    }

    &__right {
        display: flex;
        align-items: flex-end;
        gap: 14px;
        flex-wrap: wrap;
    }
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &__label {
        font-size: 10px;
        color: #94a3b8;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
}

/* Skeleton Loading */
@keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
}

.skel-card {
    flex: 1;
    min-width: 200px;
    padding: 24px 26px;
    border-radius: 14px;
    background: linear-gradient(90deg, #e8ecf1 25%, #f0f4f8 50%, #e8ecf1 75%);
    background-size: 800px 100%;
    animation: shimmer 1.6s linear infinite;
}

.skel-line {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.55);

    &--lg {
        width: 60px;
        height: 30px;
        margin-bottom: 12px;
    }

    &--sm {
        width: 100px;
        height: 12px;
    }
}

.skel-section {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    &__head {
        height: 42px;
        background: linear-gradient(90deg, #f1f5f9 25%, #f8fafc 50%, #f1f5f9 75%);
        background-size: 800px 100%;
        animation: shimmer 1.6s linear infinite;
    }
}

.skel-row {
    height: 14px;
    margin: 14px 18px;
    border-radius: 6px;
    background: linear-gradient(90deg, #f1f5f9 25%, #f8fafc 50%, #f1f5f9 75%);
    background-size: 800px 100%;
    animation: shimmer 1.6s linear infinite;

    &--short {
        width: 60%;
    }
}

.tl-loading {
    padding: 12px 0;
}

.empty-workflow {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 20px;
    color: #cbd5e1;

    &__icon {
        font-size: 56px;
        margin-bottom: 18px;
        opacity: 0.6;
    }

    &__text {
        font-size: 15px;
        margin: 0;
        color: #94a3b8;
        font-weight: 500;
    }
}

.report-body {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.section-row--cards {
    display: flex;
    gap: 18px;
}

.stat-card {
    flex: 1;
    padding: 22px 26px;
    border-radius: 14px;
    color: #fff;
    min-width: 200px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
    }

    &::after {
        content: "";
        position: absolute;
        top: -20px;
        right: -20px;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
    }

    &__number {
        font-size: 40px;
        font-weight: 800;
        line-height: 1;
        letter-spacing: -1px;
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        margin-top: 8px;
        opacity: 0.92;
        letter-spacing: 0.2px;
    }

    &__sub {
        font-size: 11px;
        margin-top: 5px;
        opacity: 0.6;
        font-weight: 500;
    }

    &--blue {
        background: linear-gradient(135deg, #4f6af6, #3b52d4);
    }

    &--orange {
        background: linear-gradient(135deg, #f59e0b, #d97706);
    }
}

.report-section {
    background: #fff;
    border-radius: 12px;
    border: none;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.07);
    }

    &__title {
        padding: 12px 18px;
        font-size: 13px;
        font-weight: 700;
        color: #334155;
        background: #f8fafc;
        border-bottom: 1px solid #f1f5f9;
        letter-spacing: 0.1px;
        text-transform: uppercase;
    }

    &__title--flex {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px;
    }

    &__count {
        font-weight: 500;
        color: #94a3b8;
        font-size: 12px;
        text-transform: none;
    }
}

.tl-inline-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.task-link {
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
        color: #4f6af6;
    }
}

.text-muted {
    color: #94a3b8;
}

.mini-empty {
    text-align: center;
    color: #94a3b8;
    font-size: 13px;
    padding: 32px 0;
    font-weight: 500;
}

/* Gantt */
.gantt-wrap {
    padding: 16px 18px;
    overflow-x: auto;
}

.gantt-legend {
    display: flex;
    gap: 16px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f1f5f9;
    flex-wrap: wrap;

    &__item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: #64748b;
        font-weight: 500;
    }

    &__dot {
        width: 10px;
        height: 10px;
        border-radius: 4px;
        flex-shrink: 0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
}

.gantt-group {
    margin-bottom: 8px;

    &__head {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0 6px;
        font-size: 12px;
        font-weight: 700;
        color: #334155;
        border-bottom: 2px solid #f1f5f9;
    }

    &__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
    }

    &__cnt {
        font-weight: 400;
        color: #94a3b8;
        font-size: 11px;
    }
}

.gantt-row {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f8fafc;
    transition: background 0.15s ease;

    &:hover {
        background: #fafbfd;
    }

    &:last-child { border-bottom: none; }

    &__label {
        width: 190px;
        flex-shrink: 0;
        padding-right: 14px;
        overflow: hidden;
    }

    &__title {
        display: block;
        font-size: 12px;
        font-weight: 600;
        color: #334155;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__id {
        font-size: 10px;
        color: #94a3b8;
        font-weight: 500;
    }

    &__bars {
        flex: 1;
        display: flex;
        height: 24px;
        gap: 1px;
        border-radius: 6px;
        overflow: hidden;
        background: #f1f5f9;
    }
}

.gantt-seg {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 4px;
    cursor: pointer;
    transition: opacity 0.15s ease, filter 0.15s ease;

    &:hover {
        opacity: 0.85;
        filter: brightness(1.08);
    }

    &__txt {
        font-size: 9px;
        color: #fff;
        font-weight: 700;
        white-space: nowrap;
        padding: 0 4px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }
}

.tl-cid {
    margin-left: 5px;
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
}

.stage-flow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    &__item {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    &__arrow {
        color: #cbd5e1;
        font-size: 12px;
    }
}

.stage-tag-ellipsis {
    max-width: 120px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    line-height: 20px;
}

::v-deep .ant-table {
    font-size: 13px;
}

::v-deep .ant-table-thead > tr > th {
    background: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    font-size: 12px;
    border-bottom: 2px solid #e2e8f0;
    padding: 10px 14px;
}

::v-deep .ant-table-tbody > tr > td {
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
}

::v-deep .ant-table-tbody > tr:hover > td {
    background: #f8fafc !important;
}

::v-deep .ant-table-bordered .ant-table-thead > tr > th,
::v-deep .ant-table-bordered .ant-table-tbody > tr > td {
    border-right: 1px solid #f1f5f9;
}

::v-deep .ant-table-bordered .ant-table-thead > tr > th {
    border-right: 1px solid #e2e8f0;
}

::v-deep .ant-tag {
    border-radius: 6px;
    font-weight: 600;
    font-size: 12px;
    border: none;
    padding: 1px 8px;
}

::v-deep .ant-pagination-item-active {
    border-color: #4f6af6;

    a {
        color: #4f6af6;
    }
}

::v-deep .ant-radio-button-wrapper-checked {
    color: #4f6af6 !important;
    border-color: #4f6af6 !important;
    box-shadow: -1px 0 0 0 #4f6af6 !important;
}
</style>